import * as React from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("America/Guatemala");

export default function MyTimePicker({
  label,
  name,
  value,
  disabled = false,
  onChange,
}) {
  const appTheme = useTheme();
  const pickerTheme = React.useMemo(
    () =>
      createTheme(appTheme, {
        palette: {
          text: {
            primary: appTheme.palette?.text?.main,
            secondary: appTheme.palette?.text?.focus || appTheme.palette?.text?.main,
          },
        },
        components: {
          MuiPickersDay: {
            styleOverrides: {
              root: {
                color: appTheme.palette.text.main,
              },
              dayWithMargin: {
                "&.Mui-selected": {
                  backgroundColor: appTheme.palette.accent.main,
                  color: appTheme.palette.white.main,
                },
                "&.Mui-selected:hover": {
                  backgroundColor: appTheme.palette.accent.focus,
                },
                "&.Mui-selected:focus": {
                  backgroundColor: appTheme.palette.accent.focus,
                },
              },
            },
          },
          MuiDayCalendar: {
            styleOverrides: {
              weekDayLabel: {
                color: appTheme.palette.text.main,
                opacity: 0.8,
                fontWeight: 600,
              },
            },
          },
          MuiPickersCalendarHeader: {
            styleOverrides: {
              label: {
                color: appTheme.palette.text.main,
                fontWeight: 700,
              },
              switchViewIcon: {
                color: appTheme.palette.accent.main,
              },
              iconButton: {
                color: appTheme.palette.accent.main,
              },
            },
          },
          MuiPickersArrowSwitcher: {
            styleOverrides: {
              root: {
                "& .MuiSvgIcon-root": {
                  color: appTheme.palette.accent.main,
                },
              },
            },
          },
          MuiClock: {
            styleOverrides: {
              clock: {
                "& .MuiClock-pin, & .MuiClock-pointer": {
                  backgroundColor: appTheme.palette.accent.main,
                },
                "& .MuiClock-amButton, & .MuiClock-pmButton": {
                  color: appTheme.palette.accent.main,
                },
              },
              numbers: {
                color: appTheme.palette.text.main,
              },
            },
          },
          MuiDigitalClockItem: {
            styleOverrides: {
              root: {
                color: appTheme.palette.text.main,
              },
              selected: {
                backgroundColor: appTheme.palette.accent.main,
                color: appTheme.palette.white.main,
                "&:hover": {
                  backgroundColor: appTheme.palette.accent.focus,
                },
              },
            },
          },
          MuiMultiSectionDigitalClockSection: {
            styleOverrides: {
              root: {
                color: appTheme.palette.text.main,
                backgroundColor: appTheme.palette.background.default,
              },
            },
          },
          MuiMultiSectionDigitalClockItem: {
            styleOverrides: {
              root: {
                color: appTheme.palette.text.main,
              },
              selected: {
                backgroundColor: appTheme.palette.accent.main,
                color: appTheme.palette.white.main,
                "&:hover": {
                  backgroundColor: appTheme.palette.accent.focus,
                },
              },
            },
          },
          MuiPickersLayout: {
            styleOverrides: {
              root: {
                color: appTheme.palette.text.main,
              },
              contentWrapper: {
                backgroundColor: appTheme.palette.background.default,
              },
            },
          },
        },
      }),
    [appTheme]
  );
  return (
    <ThemeProvider theme={pickerTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DemoContainer components={["DateTimePicker"]}>
          <DateTimePicker
            disabled={disabled}
            label={label}
            value={value}
            name={name}
            format="DD/MM/YYYY hh:mm a"
            defaultValue={dayjs.tz("2024-06-30T15:30")}
            slotProps={{
              textField: {
                sx: {
                  "& .MuiOutlinedInput-root": {
                    bgcolor: appTheme.palette.background.default,
                  },
                  "& .MuiOutlinedInput-input": {
                    color: appTheme.palette.text.main,
                  },
                  "& .MuiSvgIcon-root": {
                    color: appTheme.palette.accent.main,
                  },
                  "& .MuiInputLabel-root": {
                    color: appTheme.palette.text.main,
                  },
                },
              },
              desktopPaper: {
                sx: { bgcolor: appTheme.palette.background.default },
              },
              mobilePaper: {
                sx: { bgcolor: appTheme.palette.background.default },
              },
            }}
            onChange={(newValue) => {
              if (onChange) {
                onChange(name, dayjs.tz(newValue));
              }
            }}
          />
        </DemoContainer>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
